/* /dashboard/links — LINK REBRANDER
   Paste a long affiliate URL, pick a slug, get back faitharama.com/r/slug.
   Tracks click count per link. No tier limits.                            */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Link2, Copy, Trash2, ExternalLink, Plus, MousePointerClick } from "lucide-react";

const BRAND = "#287F8E";
const GREEN = "#2E9F3A";
const ORANGE = "#F39A1F";
const WINE = "#8B2A2A";

export default function LinksTab() {
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(true);
  const [slug, setSlug] = useState("");
  const [destination, setDestination] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/links/mine");
      setLinks(r.data?.links || []);
    } catch {
      toast.error("Couldn't load your links.");
    }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    const cleanSlug = slug.trim().toLowerCase();
    const cleanDest = destination.trim();
    if (cleanSlug.length < 3) { toast.error("Your link name needs at least 3 characters."); return; }
    if (!cleanDest.startsWith("http")) { toast.error("Destination must start with http:// or https://"); return; }
    setSaving(true);
    try {
      await api.post("/links", { slug: cleanSlug, destination: cleanDest, note: note.trim() });
      toast.success(`★ Short link created: /r/${cleanSlug}`);
      setSlug(""); setDestination(""); setNote("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the link.");
    }
    setSaving(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete short link /r/${s}? Can't undo.`)) return;
    try {
      await api.delete(`/links/${s}`);
      toast.success("Deleted.");
      await load();
    } catch {
      toast.error("Could not delete.");
    }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied.");
  };

  return (
    <div className="space-y-6 p-5 md:p-7" data-testid="links-tab">
      {/* HEADER */}
      <div className="rounded-2xl bg-white border-2 shadow-md p-5 md:p-6" style={{ borderColor: BRAND }}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: BRAND }}>
            <Link2 className="w-5 h-5"/>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: BRAND }}>★ LINK REBRANDER</div>
            <h1 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight" style={{ color: "#0F172A" }}>
              Turn Any Ugly URL Into A Faitharama Link.
            </h1>
          </div>
        </div>
        <p className="text-sm md:text-base text-foreground/80">
          Paste your affiliate URL (Goli, Amadi, Sun Home, anything). Pick a name. Get back a clean
          <span className="font-mono mx-1" style={{ color: WINE }}>{origin}/r/your-name</span>
          you can share anywhere. Every click is tracked.
        </p>
      </div>

      {/* CREATE FORM */}
      <form onSubmit={create} className="rounded-2xl bg-white border-2 shadow-md p-5 md:p-6 space-y-3"
            style={{ borderColor: GREEN }} data-testid="link-create-form">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: GREEN }}>
          ★ CREATE A NEW SHORT LINK
        </div>

        <div>
          <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: "#0F172A" }}>
            Where should the link send people? (Paste your full affiliate URL)
          </label>
          <input type="url" value={destination} onChange={(e) => setDestination(e.target.value)}
                 data-testid="link-destination-input"
                 placeholder="https://amadijewelry.com/?ref=YOURCODE"
                 className="w-full px-4 py-3 rounded-md border-2 bg-white text-base outline-none focus:ring-2 focus:ring-[#F39A1F]"
                 style={{ borderColor: `${GREEN}55` }}/>
        </div>

        <div className="grid sm:grid-cols-[1fr_2fr] gap-3">
          <div>
            <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: "#0F172A" }}>
              ★ Your Link Name
            </label>
            <div className="flex items-stretch rounded-md overflow-hidden border-2" style={{ borderColor: `${GREEN}55` }}>
              <span className="px-3 flex items-center text-xs font-mono bg-gray-100" style={{ color: "#0F172A" }}>/r/</span>
              <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)}
                     data-testid="link-slug-input"
                     placeholder="amadi-wife"
                     className="flex-1 px-3 py-3 text-base font-mono outline-none"/>
            </div>
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: "#0F172A" }}>
              Note (optional · just for you)
            </label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                   data-testid="link-note-input"
                   placeholder="Wife's birthday Amadi link"
                   className="w-full px-4 py-3 rounded-md border-2 bg-white text-base outline-none focus:ring-2 focus:ring-[#F39A1F]"
                   style={{ borderColor: `${GREEN}55` }}/>
          </div>
        </div>

        <button type="submit" disabled={saving} data-testid="link-create-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.01] transition disabled:opacity-60"
                style={{ background: GREEN }}>
          <Plus className="w-5 h-5"/> {saving ? "Creating…" : "Create Short Link"}
        </button>
      </form>

      {/* LINK LIST */}
      <div className="rounded-2xl bg-white border-2 shadow-md" style={{ borderColor: ORANGE }} data-testid="link-list">
        <div className="px-5 md:px-6 pt-5 pb-3 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: ORANGE }}>
            ★ YOUR LINKS · {links.length}
          </div>
        </div>
        {busy ? (
          <div className="px-5 md:px-6 pb-5 text-sm text-foreground/60">Loading…</div>
        ) : links.length === 0 ? (
          <div className="px-5 md:px-6 pb-5 text-sm text-foreground/60">
            No short links yet. Create your first one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ background: "#F8FAFC", color: "#5A6B7B" }}>
                <tr>
                  <th className="text-left px-4 py-2.5">Short Link</th>
                  <th className="text-left px-4 py-2.5">Destination</th>
                  <th className="text-center px-4 py-2.5">Clicks</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => {
                  const full = `${origin}/r/${l.slug}`;
                  return (
                    <tr key={l.slug} className="border-t" style={{ borderColor: "#E5E7EB" }} data-testid={`link-row-${l.slug}`}>
                      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap" style={{ color: WINE }}>{full}
                        {l.note && <div className="text-[10px] mt-0.5 text-foreground/60 font-sans">{l.note}</div>}
                      </td>
                      <td className="px-4 py-3 truncate max-w-[300px] text-xs" title={l.destination}>{l.destination}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: GREEN }}>
                          <MousePointerClick className="w-3.5 h-3.5"/> {l.clicks || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => copy(full)} title="Copy"
                                data-testid={`link-copy-${l.slug}`}
                                className="inline-flex items-center justify-center w-8 h-8 rounded mr-1"
                                style={{ background: GREEN, color: "#FFFFFF" }}>
                          <Copy className="w-4 h-4"/>
                        </button>
                        <a href={full} target="_blank" rel="noopener noreferrer" title="Open"
                           data-testid={`link-open-${l.slug}`}
                           className="inline-flex items-center justify-center w-8 h-8 rounded mr-1"
                           style={{ background: BRAND, color: "#FFFFFF" }}>
                          <ExternalLink className="w-4 h-4"/>
                        </a>
                        <button onClick={() => remove(l.slug)} title="Delete"
                                data-testid={`link-delete-${l.slug}`}
                                className="inline-flex items-center justify-center w-8 h-8 rounded"
                                style={{ background: WINE, color: "#FFFFFF" }}>
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
